import React from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    Image,
    Button,
    Grid,
    Stack,
    HStack,
    Badge,
} from "@chakra-ui/react";
import { FaArrowRight, FaHeart, FaLightbulb, FaStethoscope, FaUsers } from "react-icons/fa";
import { motion, useReducedMotion, useScroll, useSpring, useTransform, useMotionValue, MotionValue } from "framer-motion";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { Link as RouterLink } from "react-router-dom";

const team = [
    {
        name: "DR. SAM MANGER",
        title: "Co-Founder and GP",
        credential: "General Practitioner FRACGP",
        image: "/Team/Sam-Manger.png",
        // Sam's own wording, website feedback 27 Jul 2026.
        bio: "Dr Sam Manger is a multi-award winning clinical and academic GP and healthcare consultant with a focus on lifestyle and social determinants and whole person care. He is the Academic Lead of the Postgraduate Degrees in Lifestyle Medicine at James Cook University College of Medicine and Dentistry, as well as the Immediate Past-President of the Australasian Society of Lifestyle Medicine. He is an Ambassador for the Federal Government initiative Equally Well Australia, Advisor to Saje Healthcare, Advisory Group Member of the Handbook of Non-Drug Interventions at the Royal Australian College of General Practitioners and Founder of The GP Show podcast for health professionals, ranked in the top 1% of podcasts globally."
    },
    {
        name: "RONAN PAYINDA",
        title: "Co-Founder",
        credential: "Medical Student",
        image: "/Team/Ronan-Payinda.png",
        bio: "Founder of SurgReady, UNICEF Young Ambassador, and medical student in Auckland. Recognised as a Young Health Programme Impact Fellow by One Young World and AstraZeneca for work in health equity and innovation."
    },
    {
        name: "DR. THOMAS MILLER",
        title: "Co-Founder · Technology",
        credential: "Founder, Tom Miller Services",
        image: "/tomprofile.jpeg",
        bio: "Technology co-founder of SurgReady. Tom designed and runs the platform behind the program — the courses, secure sign-in and the tools that track each patient's preparation — built on Business Builder, the platform his Brisbane company Tom Miller Services develops for health and community organisations."
    }
];

// Clinical advisors — from Sam Manger's emails of 24 Oct 2026 (TMS inbox), in his order.
// Bios are the advisors' own words (Michael Inskip's 285-word bio condensed; full text in HQ).
const clinicalAdvisors = [
    {
        name: "Dr Michael Ayling",
        role: "Anaesthetist",
        credential: "Specialist anaesthetist · Lifestyle Medicine",
        image: "/Team/advisors/michael-ayling.jpg",
        bio: "Dr Michael Ayling is a specialist anaesthetist and Lifestyle Medicine physician with extensive experience in perioperative medicine. His clinical interests include surgical prehabilitation, perioperative risk reduction, behaviour change and mind–body interventions. He has postgraduate training in Lifestyle Medicine, health coaching, motivational interviewing, cognitive behavioural approaches and clinical hypnosis, with a particular interest in helping patients improve their physical and psychological preparation for surgery, recovery and longer-term health outcomes.",
    },
    {
        name: "Professor Bruce Arroll",
        role: "GP, Researcher",
        credential: "Professor of General Practice, University of Auckland",
        image: "/Team/advisors/bruce-arroll.jpg",
        bio: "Professor Bruce Arroll is Professor of General Practice and Primary Health Care at the University of Auckland, combining academic leadership with decades of general practice in South Auckland. He also practises clinically at the Calder Health Centre at Auckland City Mission, whose integrated, low-cost healthcare and social support offer compassionate, practical, accessible care to people facing significant health and social challenges.",
    },
    {
        name: "Rachel Kelly Amato",
        role: "Pharmacist",
        credential: "Community pharmacist · Master of Health & Medical Leadership",
        image: "/Team/advisors/rachel-amato.jpg",
        bio: "Rachel is a queer and neurodivergent community pharmacist with a hidden disability. Rachel holds a Graduate Diploma in Rural Generalist Practice, a Graduate Certificate in Business and a Master of Health & Medical Leadership. They are also currently completing advanced qualifications in Lifestyle Medicine. These credentials empower them to look beyond the prescription counter, blending systemic healthcare leadership with evidence-based, holistic patient care. Their special interests include genetic influences on medication, harm minimisation, preoperative optimisation and integrative health.",
    },
    {
        name: "Sharon Curtain",
        role: "Dietitian, Health Coach",
        credential: "Director, Accredited Health Coaching Australia",
        image: "/Team/advisors/sharon-curtain.jpg",
        bio: "Sharon Curtain is a Dietitian, Accredited Health Coach, Certified Trainer and Assessor, and Director of Accredited Health Coaching Australia. With almost 30 years of experience in clinical practice, healthcare, education, and behaviour change, Sharon helps individuals and health professionals develop the knowledge, confidence, and skills needed to achieve better health outcomes. Her expertise lies in translating evidence-based health information into practical, achievable actions that support lasting lifestyle change. Sharon is passionate about empowering people to take an active role in their health, make informed decisions, and build sustainable habits that improve long-term wellbeing.",
    },
    {
        name: "Dr Michael Inskip",
        role: "Exercise Physiologist",
        credential: "Accredited Exercise Physiologist · James Cook University",
        image: "/Team/advisors/michael-inskip.jpg",
        bio: "Dr Michael Inskip is a Senior Lecturer and Clinical Educator in Exercise Physiology at James Cook University and an Accredited Exercise Physiologist with more than 10 years of clinical experience. His specialist expertise is in healthy ageing, frailty, sarcopenia, dementia and evidence-based exercise and allied health services for older adults. He was Exercise Lead and Chief Investigator on the FRIEND (Frailty Reduction via Implementation of Exercise, Nutrition and Deprescribing) project in residential aged care, and brings geriatric, multidisciplinary, workforce-education and research experience to this advisory role.",
    },
    {
        name: "Christina Tibaldi Roberts",
        role: "Psychologist, Physiotherapist",
        credential: "Physiotherapist · AHPRA-registered psychologist",
        image: "/Team/advisors/christina-tibaldi-roberts.jpg",
        bio: "Christina Tibaldi Roberts is a physiotherapist and AHPRA-registered general psychologist with over three decades in healthcare across Australia and Switzerland. Combining expertise in musculoskeletal rehabilitation, pain and psychological therapies, she brings a whole-person approach to surgical preparation and recovery. At SurgReady, Christina helps patients build strategies to support physical recovery, emotional wellbeing and confidence throughout their surgical journey. She also holds a Master of Health Sciences in Orthopaedic Manipulative Therapy and speaks English, French and German.",
    },
];

// One advisor card. Module scope (never inside About) so it isn't remounted on re-render.
const AdvisorCard: React.FC<{ advisor: typeof clinicalAdvisors[number] }> = ({ advisor }) => {
    const [open, setOpen] = React.useState(false);
    return (
        <Box bg="#0f1112" color={TEXT_LIGHT_C} overflow="hidden" border="1px solid rgba(255,255,255,0.06)" display="flex" flexDirection="column">
            <Box h={{ base: "300px", md: "320px" }} w="100%" overflow="hidden" bg="#0b0d0f">
                <Image src={advisor.image} alt={advisor.name} objectFit="cover" objectPosition="center 20%" w="100%" h="100%" loading="lazy" />
            </Box>
            <Box p={{ base: 5, md: 6 }} display="flex" flexDirection="column" flex="1">
                <Text fontSize="sm" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase" color={ACCENT_C} mb={2}>
                    {advisor.role}
                </Text>
                <Heading as="h4" fontSize={{ base: "1.35rem", md: "1.6rem" }} lineHeight={1.1} letterSpacing="-0.03em" fontWeight={600} mb={3}>
                    {advisor.name}
                </Heading>
                <Badge alignSelf="flex-start" whiteSpace="normal" textTransform="none" fontSize="sm" fontWeight={500} bg="rgba(122,176,255,0.08)" border="1px solid rgba(122,176,255,0.14)" color="#d7ebff" borderRadius="full" px={3} py={1} mb={4}>
                    {advisor.credential}
                </Badge>
                <Text fontSize="md" lineHeight="1.7" color="rgba(247,242,236,0.82)" noOfLines={open ? undefined : 5}>
                    {advisor.bio}
                </Text>
                <Button mt={3} alignSelf="flex-start" variant="link" color={ACCENT_C} fontSize="md" minH="44px" onClick={() => setOpen(!open)} aria-expanded={open}>
                    {open ? "Show less" : "Read more"}
                </Button>
            </Box>
        </Box>
    );
};

const ACCENT_C = "#7AB0FF";
const TEXT_LIGHT_C = "#f7f2ec";

// Module scope: inside About, each re-render made a new component type and remounted every MotionBox.
const MotionBox = motion(Box);

type AtmosphericHeartBackgroundProps = {
    heroRef: React.RefObject<HTMLDivElement | null>;
    heroScroll: MotionValue<number>;
    prefersReducedMotion: boolean | null;
};

// Module scope: defined inside About it was remounted on every About re-render, popping the glow.
const AtmosphericHeartBackground: React.FC<AtmosphericHeartBackgroundProps> = ({ heroRef, heroScroll, prefersReducedMotion }) => {
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    React.useEffect(() => {
        const node = heroRef.current;
        if (!node) return;

        const handlePointerMove = (event: PointerEvent) => {
            const rect = node.getBoundingClientRect();
            const px = ((event.clientX - rect.left) / rect.width - 0.5) * 24;
            const py = ((event.clientY - rect.top) / rect.height - 0.5) * 18;
            mouseX.set(px);
            mouseY.set(py);
        };

        node.addEventListener('pointermove', handlePointerMove);
        return () => node.removeEventListener('pointermove', handlePointerMove);
    }, [heroRef, mouseX, mouseY]);

    const shiftX = useTransform(mouseX, [-24, 24], [-14, 14]);
    const shiftY = useTransform(mouseY, [-18, 18], [-10, 10]);
    const opacity = useTransform(heroScroll, [0, 0.12, 0.4, 1], [0.12, 0.26, 0.38, 0.28]);
    const scale = useTransform(heroScroll, [0, 0.25, 0.75, 1], [0.92, 1, 1.04, 1.02]);

    const heartAnimation = prefersReducedMotion
        ? undefined
        : {
            scale: [1, 1.026, 1, 1.03, 1],
            opacity: [0.32, 0.46, 0.36, 0.5, 0.32],
        };

    return (
        <MotionBox
            aria-hidden
            pointerEvents="none"
            position="absolute"
            inset={0}
            zIndex={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            style={{ opacity, x: shiftX, y: shiftY, scale }}
        >
            <MotionBox
                position="absolute"
                width={{ base: '92vw', md: '820px' }}
                height={{ base: '56vh', md: '760px' }}
                pointerEvents="none"
                display="flex"
                alignItems="center"
                justifyContent="center"
                animate={heartAnimation}
                transition={{ duration: 3, ease: 'easeInOut', repeat: Infinity }}
                style={{ willChange: 'transform, opacity' }}
            >
                <Box
                    position="absolute"
                    width="82%"
                    height="82%"
                    bg="radial-gradient(circle at 50% 46%, rgba(255,213,222,0.26) 0%, rgba(255,168,186,0.18) 18%, rgba(201,70,88,0.08) 38%, rgba(13,17,20,0) 70%)"
                    filter="blur(36px)"
                />
                <svg
                    viewBox="0 0 1000 1000"
                    width="100%"
                    height="100%"
                    preserveAspectRatio="xMidYMid meet"
                    style={{ overflow: 'visible', filter: 'blur(9px)', opacity: 1 }}
                >
                    <defs>
                        <radialGradient id="heartGlowA" cx="50%" cy="35%" r="65%">
                            <stop offset="0%" stopColor="rgba(255,245,248,1)" />
                            <stop offset="20%" stopColor="rgba(255,150,175,1)" />
                            <stop offset="42%" stopColor="rgba(235,75,105,0.9)" />
                            <stop offset="72%" stopColor="rgba(190,45,70,0.45)" />
                            <stop offset="100%" stopColor="rgba(30,18,21,0)" />
                        </radialGradient>
                        <radialGradient id="heartGlowB" cx="50%" cy="38%" r="70%">
                            <stop offset="0%" stopColor="rgba(255,245,248,1)" />
                            <stop offset="22%" stopColor="rgba(255,165,185,0.95)" />
                            <stop offset="58%" stopColor="rgba(225,65,95,0.6)" />
                            <stop offset="100%" stopColor="rgba(30,18,21,0)" />
                        </radialGradient>
                    </defs>
                    <g>
                        <path
                            d="M500 170C625 20 910 40 910 290C910 495 760 610 500 835C240 610 90 495 90 290C90 40 375 20 500 170Z"
                            fill="url(#heartGlowA)"
                            opacity="1"
                        />
                        <path
                            d="M500 235C605 120 805 145 805 302C805 438 705 528 500 690C295 528 195 438 195 302C195 145 395 120 500 235Z"
                            fill="url(#heartGlowB)"
                            opacity="1"
                        />
                    </g>
                </svg>
                <Box
                    position="absolute"
                    width="62%"
                    height="62%"
                    bg="radial-gradient(circle at 50% 46%, rgba(255,213,222,0.5) 0%, rgba(255,120,150,0.35) 18%, rgba(201,50,80,0.18) 38%, rgba(13,17,20,0) 70%)"
                    filter="blur(28px)"
                />
            </MotionBox>
        </MotionBox>
    );
};

const About = () => {
    const prefersReducedMotion = useReducedMotion();
    const ACCENT = "#7AB0FF";
    const BG_DARK = "#0b0f14";
    const BG_DARK_3 = "#0f1318";
    const TEXT_LIGHT = "#f7f2ec";
    const heroRef = React.useRef<HTMLDivElement | null>(null);
    const { scrollYProgress: heroScroll } = useScroll({
        target: heroRef,
        offset: ["start end", "end start"]
    });

    const heroBlobOpacity = useTransform(heroScroll, [0, 0.06, 0.18, 0.36, 1], [0, 0.35, 0.95, 0.85, 0]);
    const heroBlobX = useTransform(heroScroll, [0, 1], [-60, 40]);
    const heroBlobY = useTransform(heroScroll, [0, 1], [40, -20]);
    const heroBlobScale = useTransform(heroScroll, [0, 0.12, 0.28, 0.45, 1], [0.6, 0.95, 1.8, 1.25, 0.9]);



    const pageProgress = useScroll();
    const smoothProgress = useSpring(pageProgress.scrollYProgress, { stiffness: 140, damping: 25, mass: 0.4 });

    const heroOpacity = useTransform(smoothProgress, [0, 0.12], [1, 0.7]);
    const heroY = useTransform(smoothProgress, [0, 0.18], [0, -32]);
    const introOpacity = useTransform(smoothProgress, [0.1, 0.28], [0, 1]);
    const introY = useTransform(smoothProgress, [0.1, 0.28], [36, 0]);

    return (
        <Box bg={BG_DARK} minH="100vh" color={TEXT_LIGHT}>
            <NavbarWithCallToAction />

            <Box as="section" id="about" ref={heroRef} bg="#0b0f14" color="#f7f2ec" overflow="hidden" position="relative">
                <AtmosphericHeartBackground heroRef={heroRef} heroScroll={heroScroll} prefersReducedMotion={prefersReducedMotion} />
                <Container maxW="1360px" minH="100vh" display="flex" alignItems="center" py={{ base: 12, md: 18 }}>
                    <MotionBox style={prefersReducedMotion ? undefined : { opacity: heroOpacity, y: heroY }} width="100%">
                        <Text fontSize="sm" fontWeight="700" letterSpacing="0.18em" textTransform="uppercase" color="rgba(247,242,236,0.7)" mb={6}>
                            Our story
                        </Text>

                        <Heading
                            as="h1"
                            fontSize={{ base: "3rem", md: "4.3rem", lg: "6.2rem" }}
                            lineHeight={0.92}
                            letterSpacing="-0.04em"
                            maxW="980px"
                            fontWeight={400}
                            mb={7}
                        >
                            Optimising health
                            <br />
                            before surgery.
                        </Heading>

                        <Text fontSize={{ base: "lg", md: "2xl" }} color="rgba(247,242,236,0.72)" maxW="720px" lineHeight="1.7">
                            Every year there are around 2 million surgeries performed across Australia and New Zealand.
                            But many people go into surgery unprepared, meaning higher complications, longer hospital
                            stays, and more anxiety and pain.
                        </Text>
                    </MotionBox>
                </Container>
            </Box>

            <Box bg="#f6fbff" py={{ base: 14, md: 20 }}>
                <Container maxW="1280px">
                    <MotionBox
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: false, amount: 0.18, margin: "0px 0px -30% 0px" }}
                        transition={{ duration: 0.45, ease: "easeOut" }}
                        maxW="980px"
                    >
                        <Text fontSize="sm" fontWeight="700" letterSpacing="0.18em" textTransform="uppercase" color={ACCENT} mb={4}>
                            Why we're doing this
                        </Text>
                        {/* Sam Manger's draft story and mission, website feedback 27 Jul 2026. */}
                        <Text fontSize={{ base: "lg", md: "xl" }} color="#0b1720" lineHeight="1.8" maxW="880px" mb={5}>
                            By optimising our nutrition, strength, fitness, mental health, social connection, supplements,
                            medications and chronic diseases, we can give the body and mind what it needs to be ready, heal,
                            recover and return to doing what we love most.
                        </Text>
                        <Text fontSize={{ base: "lg", md: "xl" }} color="#0b1720" lineHeight="1.8" maxW="880px" mb={8}>
                            SurgReady was founded in 2025 by a multidisciplinary team who recognised the critical gap in
                            preoperative patient preparation. After years of witnessing patients arrive for surgery unprepared
                            and experiencing preventable complications, our founders committed to creating a comprehensive,
                            evidence-informed solution.
                        </Text>
                        <Text fontSize="sm" fontWeight="700" letterSpacing="0.18em" textTransform="uppercase" color={ACCENT} mb={4}>
                            Our mission
                        </Text>
                        <Heading
                            as="h2"
                            fontSize={{ base: "1.6rem", md: "2.4rem" }}
                            lineHeight={1.15}
                            letterSpacing="-0.02em"
                            fontWeight={600}
                            color="#051226"
                        >
                            To empower people undergoing surgery through a comprehensive multidisciplinary team-based program.
                        </Heading>
                    </MotionBox>
                </Container>
            </Box>

            <Box bg={BG_DARK_3} py={{ base: 14, md: 20 }}>
                <Container maxW="1280px">
                    <MotionBox
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: false, amount: 0.7, margin: "0px 0px -12% 0px" }}
                        transition={{ duration: 0.45, ease: "easeOut" }}
                        mb={10}
                    >
                        <Text fontSize="sm" fontWeight="700" letterSpacing="0.18em" textTransform="uppercase" color={ACCENT} mb={4}>
                            Meet our team
                        </Text>
                        <Heading as="h3" fontSize={{ base: "2.5rem", md: "4.5rem" }} lineHeight={0.96} letterSpacing="-0.065em" fontWeight={400} maxW="980px" color={TEXT_LIGHT}>
                            A multidisciplinary team working to make surgical preparation clearer, calmer and more effective.
                        </Heading>
                    </MotionBox>

                    <Grid templateColumns={{ base: "1fr", md: "repeat(2,1fr)", lg: "repeat(3,1fr)" }} gap={8}>
                        {team.map((person, index) => (
                            <MotionBox
                                key={person.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: false, amount: 0.6 }}
                                transition={{ duration: 0.42, delay: index * 0.06, ease: "easeOut" }}
                                bg="#0f1112"
                                color={TEXT_LIGHT}
                                borderRadius="0"
                                overflow="hidden"
                                border="1px solid rgba(255,255,255,0.06)"
                            >
                                <Box h={{ base: "280px", md: "340px" }} w="100%" overflow="hidden" bg="#0b0d0f">
                                    {person.image ? (
                                        <Image
                                            src={person.image}
                                            alt={person.name}
                                            objectFit="cover"
                                            objectPosition={person.name === "DR. SAM MANGER" ? "center 24%" : person.name === "RONAN PAYINDA" ? "center 24%" : "center center"}
                                            w="100%"
                                            h="100%"
                                            transform={person.name === "DR. SAM MANGER" || person.name === "RONAN PAYINDA" ? "scale(0.88)" : undefined}
                                        />
                                    ) : (
                                        <Box w="100%" h="100%" bg="rgba(255,255,255,0.03)" />
                                    )}
                                </Box>
                                <Box p={{ base: 4, md: 6 }}>
                                    <Text fontSize="sm" fontWeight="700" letterSpacing="0.14em" textTransform="uppercase" color={ACCENT} mb={2}>
                                        Team
                                    </Text>
                                    <Heading as="h4" fontSize={{ base: "1.25rem", md: "1.8rem" }} lineHeight={1.08} letterSpacing="-0.03em" fontWeight={600} color={TEXT_LIGHT} mb={2}>
                                        {person.name}
                                    </Heading>
                                    <Text fontSize={{ base: "0.95rem", md: "1.05rem" }} color="rgba(247,242,236,0.78)" fontWeight={500} mb={3}>
                                        {person.title}
                                    </Text>
                                    <Badge bg="rgba(122,176,255,0.08)" border="1px solid rgba(122,176,255,0.14)" color="#d7ebff" borderRadius="full" px={3} py={1} fontSize="sm" textTransform="none" whiteSpace="normal">
                                        {person.credential}
                                    </Badge>
                                    <Text mt={4} fontSize="md" lineHeight="1.7" color="rgba(247,242,236,0.82)">
                                        {person.bio}
                                    </Text>
                                </Box>
                            </MotionBox>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box bg={BG_DARK_3} pb={{ base: 14, md: 20 }} id="clinical-advisors">
                <Container maxW="1280px">
                    <Box mb={10} pt={{ base: 2, md: 4 }} borderTop="1px solid rgba(255,255,255,0.06)">
                        <Text fontSize="sm" fontWeight="700" letterSpacing="0.18em" textTransform="uppercase" color={ACCENT} mb={4} mt={{ base: 10, md: 14 }}>
                            Advisory committee
                        </Text>
                        <Heading as="h3" fontSize={{ base: "2rem", md: "3.25rem" }} lineHeight={1} letterSpacing="-0.05em" fontWeight={400} maxW="980px" color={TEXT_LIGHT}>
                            Specialists who help shape the program.
                        </Heading>
                    </Box>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(2,1fr)", lg: "repeat(3,1fr)" }} gap={8}>
                        {clinicalAdvisors.map((advisor) => (
                            <AdvisorCard key={advisor.name} advisor={advisor} />
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box bg="#111111" color="#f7f2ec" py={{ base: 14, md: 18 }}>
                <Container maxW="1280px">
                    <MotionBox
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: false, amount: 0.7, margin: "0px 0px -10% 0px" }}
                        transition={{ duration: 0.45, ease: "easeOut" }}
                        textAlign="center"
                    >
                        <Text fontSize="sm" fontWeight="700" letterSpacing="0.18em" textTransform="uppercase" color="rgba(247,242,236,0.7)" mb={5}>
                            Ready when you are
                        </Text>
                        <Heading as="h3" fontSize={{ base: "2.5rem", md: "5rem" }} lineHeight={0.94} letterSpacing="-0.065em" fontWeight={400} maxW="980px" mx="auto" mb={8}>
                            Let’s help patients feel more prepared before surgery begins.
                        </Heading>
                        <Stack direction={{ base: "column", sm: "row" }} spacing={4} justify="center" align="center">
                            {/* Sam (27 Jul 2026): contact shouldn't be central until the virtual health clinic exists. */}
                            <Button as={RouterLink} to="/pricing" size="lg" minH="48px" bg={ACCENT} color={BG_DARK} _hover={{ bg: "#5F98FF" }} rightIcon={<FaArrowRight />} fontWeight={700}>
                                Get SurgReady
                            </Button>
                            <Button as={RouterLink} to="/how-it-works" size="lg" minH="48px" variant="outline" borderColor="rgba(255,255,255,0.2)" color="#f7f2ec" _hover={{ bg: "rgba(255,255,255,0.05)" }} fontWeight={700}>
                                See how it works
                            </Button>
                        </Stack>
                    </MotionBox>
                </Container>
            </Box>

            <FooterWithFourColumns />
        </Box>
    );
};

export default About;